import { sendGTMEvent } from '@next/third-parties/google';
import { Routes } from '@/config/routes';
import { siteSettings } from '@/config/site';
import { CONSENT_POPUP_MODAL_KEY } from '@/lib/constants';
import { FaroEventName, faroTrackFromPayload } from '@/lib/grafana-faro-events';
import { SEARCH_PAGE_CATEGORY_KEY } from '@/lib/services/typesense/constants';
import { isNativeApp } from '@/lib/utils/native-communication';
import Cookies from 'js-cookie';
import { isMobile } from 'react-device-detect';

/**
 * Remember that the LGPD notice was dismissed. Google storage is granted in
 * the document bootstrap for every visitor, so this must not send a consent
 * command.
 */
export const markConsentNoticeSeen = () => {
  if (typeof window === 'undefined') return;
  Cookies.set(CONSENT_POPUP_MODAL_KEY, 'true', {
    expires: 365,
  });
};

/**
 * Helper function to detect if user is on mobile (either native app or mobile browser)
 */
export const isMobileDevice = (): boolean => {
  if (typeof window === 'undefined') return false;
  return isNativeApp() || isMobile;
};

/**
 * Helper function to get full URL from relative path
 */
export const getFullUrl = (path: string): string => {
  if (typeof window === 'undefined') return path;
  if (path.startsWith('http://') || path.startsWith('https://')) return path;
  return `${window.location.origin}${path}`;
};

/**
 * Send a custom event to Google Tag Manager using the official helper.
 * Example:
 *   gtmEvent({ event: 'signup', method: 'Google' })
 */
export const gtmEvent = (data: Record<string, any>) => {
  if (typeof window === 'undefined') return;
  try {
    sendGTMEvent(data);
  } catch (err) {
    console.warn('[GTM] Failed to send event:', err);
  }
};

/**
 * Send a pageview event to Google Tag Manager for SPA navigation.
 * Example: gtmPageView('/dashboard')
 */
export const gtmPageView = (url: string) => {
  gtmEvent({
    event: 'pageview',
    page: url,
  });
};

// Types for ecommerce events
export interface GTMItem {
  item_name: string;
  item_id: string;
  item_sku: string;
  price: number;
  sale_price: number;
  item_brand: string;
  quantity: number;
  variant_id: string; // product SKU
  product_url: string; // full URL
  product_image: string;
  category: string;
}

/**
 * Helper function to convert cart/product items to GTM format
 */
export const productToGTMItem = (
  product: any,
  quantity = 1,
  categories: any[] = [],
  brand: string = '',
): GTMItem => {
  const productId =
    product.product_id?.toString() || product.id?.toString() || '';
  const variantId = product.sku?.toString() || productId;
  const price = product.price || 0;
  const salePrice = product.sale_price || price;
  const relativePath =
    product.slug && (product.sku || productId)
      ? Routes.product(product.slug, product.sku || productId)
      : '';
  const productUrl = getFullUrl(relativePath);
  const productImage =
    product.image?.original ||
    product.image?.thumbnail ||
    product.gallery?.[0]?.original ||
    product.image ||
    '';
  // Category: use explicit param first, then product.categories array, then product.category string
  const category =
    categories?.[0]?.name ||
    product.categories?.[0]?.name ||
    product.category ||
    '';
  // Brand: use explicit param first, then product.manufacturer (object or string), then product.brand
  const itemBrand =
    brand ||
    product.manufacturer?.name ||
    (typeof product.manufacturer === 'string' ? product.manufacturer : '') ||
    product.brand ||
    '';

  return {
    item_name: product.name || '',
    item_id: productId,
    item_sku: product.sku || '',
    price: price,
    sale_price: salePrice,
    item_brand: itemBrand,
    quantity: quantity || product.quantity || 1,
    variant_id: variantId,
    product_url: productUrl,
    product_image: productImage,
    category,
  };
};

/**
 * Send a view_item event when a user views a product.
 * Example:
 *   gtmViewItem({
 *     product_id: '123',
 *     product_name: 'Product Name',
 *     brand: 'Brand Name',
 *     category: 'Category Name',
 *     price: 99.99,
 *     sale_price: 79.99,
 *     product_image: 'https://example.com/image.jpg',
 *     product_url: '/product-slug/product/123',
 *     currency: 'BRL',
 *   })
 */
export interface GTMViewItemData {
  product_id: string;
  product_name: string;
  brand: string;
  category: string;
  price: number;
  sale_price: number;
  product_image: string;
  product_url: string;
  currency: string;
  variant_id: string; // product SKU
}

/**
 * Helper function to convert product to GTM view_item format
 * Example:
 *   productToGTMViewItem(product)
 */
export const productToGTMViewItem = (product: any): GTMViewItemData => {
  const productId =
    product.id?.toString() ||
    product.sku?.toString() ||
    product.product_id?.toString() ||
    '';
  const productName = product.name || '';
  const brand = product.manufacturer?.name || product.brand || '';
  const category = product.categories?.[0]?.name || product.category || '';
  const price = product.price || 0;
  const salePrice = product.sale_price || price;
  const productImage =
    product.image?.original ||
    product.image?.thumbnail ||
    product.gallery?.[0]?.original ||
    '';
  const relativePath =
    product.slug && productId ? Routes.product(product.slug, productId) : '';
  const productUrl = getFullUrl(relativePath);
  const currency = siteSettings.currencyCode || 'BRL';
  const variantId = product.sku?.toString() || productId;

  return {
    product_id: productId,
    product_name: productName,
    brand: brand,
    category: category,
    price: price,
    sale_price: salePrice,
    product_image: productImage,
    product_url: productUrl,
    currency: currency,
    variant_id: variantId,
  };
};

export const gtmViewItem = (data: GTMViewItemData) => {
  gtmEvent({
    event: 'view_item',
    ...data,
  });
};

/**
 * Send a register event when a user registers.
 * Example:
 *   gtmRegister({
 *     cpf: '12345678900',
 *     email: 'user@example.com',
 *     first_name: 'John',
 *     last_name: 'Doe',
 *     gender: 'male',
 *     phone: '11999999999',
 *     social_name: 'Johnny',
 *     birth_date: '1990-01-01',
 *   })
 */
export interface GTMRegisterData {
  cpf: string;
  email: string;
  first_name: string;
  last_name: string;
  gender: string;
  phone: string;
  social_name?: string;
  birth_date: string;
  mobile: boolean;
}

export const gtmRegister = (data: GTMRegisterData) => {
  gtmEvent({
    event: 'register',
    ...data,
  });
  faroTrackFromPayload(
    FaroEventName.REGISTER,
    { ...data, mobile: data.mobile },
    'auth',
  );
};

/**
 * Send a login event when a user logs in.
 * Example:
 *   gtmLogin({
 *     cpf: '12345678900',
 *     email: 'user@example.com',
 *   })
 */
export interface GTMLoginData {
  cpf: string;
  email: string;
}

export const gtmLogin = (data: GTMLoginData) => {
  gtmEvent({
    event: 'log_in',
    ...data,
  });
  faroTrackFromPayload(FaroEventName.LOGIN, { ...data }, 'auth');
};

/**
 * Send a sign_up event when a user registers (legacy - use gtmRegister instead).
 * @deprecated Use gtmRegister instead
 */
export const gtmSignUp = () => {
  gtmEvent({
    event: 'sign_up',
  });
};

/**
 * Send an update_profile event when a user updates their profile.
 * Example:
 *   gtmUpdateProfile({
 *     cpf: '12345678900',
 *     email: 'user@example.com',
 *     first_name: 'John',
 *     last_name: 'Doe',
 *     gender: 'male',
 *     phone: '11999999999',
 *     social_name: 'Johnny',
 *     birth_date: '1990-01-01',
 *   })
 */
export interface GTMUpdateProfileData {
  cpf: string;
  email: string;
  first_name: string;
  last_name: string;
  gender: string;
  phone: string;
  social_name?: string;
  birth_date: string;
  mobile: boolean;
}

export const gtmUpdateProfile = (data: GTMUpdateProfileData) => {
  gtmEvent({
    event: 'update_profile',
    ...data,
  });
};

/**
 * Send a category_view event when a user views a category.
 * Example:
 *   gtmCategoryView({
 *     category: 'Vitamins',
 *     category_url: '/search?products[refinementList][categories.name][0]=Vitamins',
 *   })
 */
export interface GTMCategoryViewData {
  category: string;
  category_url: string;
}

export const buildCategorySearchUrl = (categoryName: string): string =>
  `/search?${SEARCH_PAGE_CATEGORY_KEY}=${encodeURIComponent(categoryName)}`;

export const gtmCategoryView = (data: GTMCategoryViewData) => {
  gtmEvent({
    event: 'category_view',
    ...data,
  });
};

export const trackCategoryView = (categoryName: string) => {
  gtmCategoryView({
    category: categoryName,
    category_url: buildCategorySearchUrl(categoryName),
  });
};

/**
 * Send an add_to_cart event when a user adds a product to cart.
 * Example:
 *   gtmAddToCart({
 *     ecommerce: {
 *       currency: 'BRL',
 *       value: 99.99,
 *       items: [
 *         {
 *           item_id: '123',
 *           item_name: 'Product Name',
 *           item_brand: 'Brand Name',
 *           category: 'Category Name',
 *           price: 99.99,
 *           sale_price: 79.99,
 *           product_image: 'https://example.com/image.jpg',
 *           product_url: '/product-slug/product/123',
 *           currency: 'BRL',
 *           quantity: 1,
 *           variant_id: '123',
 *         },
 *       ],
 *     },
 *   })
 */

export interface GTMAddToCartData {
  ecommerce: {
    currency: string;
    value: number;
    items: GTMItem[];
  };
  /** Identifies where the add-to-cart action originated (e.g. buy_together). */
  add_to_cart_source?: string;
  /** Primary product id when the action is tied to a PDP context. */
  main_product_id?: string;
}
export const gtmAddToCart = (data: GTMAddToCartData) => {
  gtmEvent(
    withHomeAttribution({
      event: 'add_to_cart',
      ...data,
    }),
  );
  faroTrackFromPayload(
    FaroEventName.ADD_TO_CART,
    { ecommerce: data.ecommerce },
    'ecommerce',
  );
};

/**
 * Send a remove_from_cart event when a user removes a product from cart.
 * Example:
 *   gtmRemoveFromCart({
 *     ecommerce: {
 *       currency: 'BRL',
 *       value: 99.99,
 *       items: [
 *         {
 *           item_id: '123',
 *           item_name: 'Product Name',
 *           item_brand: 'Brand Name',
 *           category: 'Category Name',
 *           price: 99.99,
 *           sale_price: 79.99,
 *           product_image: 'https://example.com/image.jpg',
 *           product_url: '/product-slug/product/123',
 *           currency: 'BRL',
 *           quantity: 1,
 *           variant_id: '123',
 *         },
 *       ],
 *     },
 *   })
 */
export interface GTMRemoveFromCartData {
  ecommerce: {
    currency: string;
    value: number;
    items: GTMItem[];
  };
}

export const gtmRemoveFromCart = (data: GTMRemoveFromCartData) => {
  gtmEvent({
    event: 'remove_from_cart',
    ...data,
  });
  faroTrackFromPayload(
    FaroEventName.REMOVE_FROM_CART,
    { ecommerce: data.ecommerce },
    'ecommerce',
  );
};

/**
 * Helper function to get payment method name from gateway ID
 */
export const getPaymentMethodName = (gatewayId?: number): string => {
  if (!gatewayId) return '';

  const paymentMethodMap: Record<number, string> = {
    3: 'Carteira',
    4: 'Pix',
    5: 'Cartão de Crédito',
    6: 'Picpay',
    7: 'Mercado Pago',
  };

  return paymentMethodMap[gatewayId] || '';
};

/**
 * Send a purchase event when a user completes a purchase.
 * Example:
 *   gtmPurchase({
 *     ecommerce: {
 *       currency: 'BRL',
 *       value: 109.99,
 *       total_items: 3,
 *       items: [{ item_name: 'Product', item_id: '123', ... }],
 *       payment_method: 'Pix',
 *       cpf: '12345678900',
 *       email: 'user@example.com',
 *       coupon: 'COUPON123',
 *       transaction_id: 'ORDER123',
 *       shipping: 10.00,
 *       tax: 10.00,
 *     },
 *   })
 */
export interface GTMPurchaseParams {
  ecommerce: {
    currency: string;
    value: number;
    total_items: number;
    items: GTMItem[];
    payment_method?: string; // Payment method name (e.g., 'Pix', 'Cartão de Crédito')
    cpf?: string; // User CPF
    email?: string; // User email
    coupon?: string; // Coupon code entered by user
    transaction_id?: string;
    shipping?: number;
    tax?: number;
  };
}

export const gtmPurchase = (params: GTMPurchaseParams) => {
  gtmEvent(
    withHomeAttribution({
      event: 'purchase',
      ...params,
    }),
  );
  clearHomeAttribution();
  faroTrackFromPayload(
    FaroEventName.PLACE_ORDER,
    { ecommerce: params.ecommerce },
    'ecommerce',
  );
};

/**
 * Send a begin_checkout event when a user starts the checkout process.
 * Example:
 *   gtmBeginCheckout({
 *     ecommerce: {
 *       currency: 'BRL',
 *       value: 109.99,
 *       total_items: 3,
 *       items: [{ item_name: 'Product', item_id: '123', ... }],
 *     },
 *   })
 */
export interface GTMBeginCheckoutParams {
  ecommerce: {
    currency: string;
    value: number;
    total_items: number;
    items: GTMItem[];
  };
}

export const gtmBeginCheckout = (params: GTMBeginCheckoutParams) => {
  gtmEvent({
    event: 'begin_checkout',
    ...params,
  });
  faroTrackFromPayload(
    FaroEventName.CHECKOUT_INIT,
    { ecommerce: params.ecommerce },
    'ecommerce',
  );
};

/** Checkout coupon picker opened (from the summary card or "Trocar"). */
export const gtmCouponPickerOpened = (params: {
  available_count: number;
  locked_count: number;
  trigger: 'view_all' | 'swap';
}) => {
  gtmEvent({ event: 'coupon_picker_opened', ...params });
};

/** Customer chose a listed coupon; `rank` is 1-based among usable coupons. */
export const gtmCouponSelected = (params: {
  coupon_code: string;
  coupon_source: string;
  rank: number;
  is_best: boolean;
  placement: 'summary_card' | 'picker';
}) => {
  gtmEvent({ event: 'coupon_selected', ...params });
};

/** A coupon chosen from the list passed verification and is now applied. */
export const gtmCouponAppliedFromList = (params: {
  coupon_code: string;
  coupon_source: string;
  savings: number | null;
}) => {
  gtmEvent({ event: 'coupon_applied_from_list', ...params });
};

/**
 * Send a search event when a user performs a search.
 * Example:
 *   gtmSearch('vitamins')
 */
export const gtmSearch = (searchTerm: string) => {
  gtmEvent({
    event: 'search',
    search_term: searchTerm,
  });
};

/**
 * Send a purchase_intent event when a user clicks the checkout button.
 * This event captures the user's intent to purchase before the order is submitted,
 * useful for tracking checkout button clicks even if the order fails.
 * Example:
 *   gtmPurchaseIntent({
 *     ecommerce: {
 *       currency: 'BRL',
 *       value: 109.99,
 *       total_items: 3,
 *       items: [{ item_name: 'Product', item_id: '123', ... }],
 *       payment_method: 'Pix',
 *       coupon: 'COUPON123',
 *     },
 *   })
 */
export interface GTMPurchaseIntentParams {
  ecommerce: {
    currency: string;
    value: number;
    total_items: number;
    items: GTMItem[];
    payment_method?: string; // Payment method name (e.g., 'Pix', 'Cartão de Crédito')
    coupon?: string; // Coupon code entered by user
  };
}

export const gtmPurchaseIntent = (params: GTMPurchaseIntentParams) => {
  gtmEvent({
    event: 'purchase_intent',
    ...params,
  });
};

// ---------------------------------------------------------------------------
// Home section attribution (sessionStorage bridge for click → cart → purchase)
// ---------------------------------------------------------------------------

const HOME_ATTRIBUTION_KEY = 'qualiapp_home_attribution';

export interface HomeAttribution {
  section_id: string;
  section_type: string;
  section_title: string;
  location_id: string;
  creative_slot?: string;
  creative_name?: string;
  destination_url?: string;
  item_list_id?: string;
  item_list_name?: string;
  clicked_at: number;
}

export type HomeListContext = {
  sectionId?: string;
  sectionType?: string;
  sectionTitle?: string;
  locationId?: string;
};

export const resolveSectionId = (
  section?: { id?: string; type?: string; order?: number },
): string => {
  if (section?.id) return section.id;
  if (section?.type != null && section?.order != null) {
    return `${section.type}-${section.order}`;
  }
  return 'unknown-section';
};

export const buildHomeListContext = (
  section?: {
    id?: string;
    type?: string;
    title?: string;
    order?: number;
  },
  locationId = 'home',
): HomeListContext => ({
  sectionId: resolveSectionId(section),
  sectionType: section?.type || 'unknown',
  sectionTitle: section?.title || '',
  locationId,
});

const readHomeAttribution = (): HomeAttribution | null => {
  if (typeof window === 'undefined') return null;
  try {
    const raw = sessionStorage.getItem(HOME_ATTRIBUTION_KEY);
    if (!raw) return null;
    return JSON.parse(raw) as HomeAttribution;
  } catch {
    return null;
  }
};

export const setHomeAttribution = (data: Omit<HomeAttribution, 'clicked_at'>) => {
  if (typeof window === 'undefined') return;
  try {
    sessionStorage.setItem(
      HOME_ATTRIBUTION_KEY,
      JSON.stringify({ ...data, clicked_at: Date.now() }),
    );
  } catch {
    // ignore quota / private mode
  }
};

export const getHomeAttribution = (): HomeAttribution | null =>
  readHomeAttribution();

export const clearHomeAttribution = () => {
  if (typeof window === 'undefined') return;
  try {
    sessionStorage.removeItem(HOME_ATTRIBUTION_KEY);
  } catch {
    // ignore
  }
};

export const applyListContextAttribution = (context?: HomeListContext) => {
  if (!context?.sectionId) return;
  setHomeAttribution({
    section_id: context.sectionId,
    section_type: context.sectionType || 'unknown',
    section_title: context.sectionTitle || '',
    location_id: context.locationId || 'home',
    item_list_id: context.sectionId,
    item_list_name: context.sectionTitle || '',
  });
};

const withHomeAttribution = <T extends Record<string, unknown>>(payload: T) => {
  const attribution = getHomeAttribution();
  if (!attribution) return payload;
  return { ...payload, home_attribution: attribution };
};

export interface GTMSelectPromotionData {
  promotion_id: string;
  promotion_name: string;
  creative_name?: string;
  creative_slot?: string;
  location_id?: string;
  destination_url?: string;
  section_type?: string;
}

export const gtmSelectPromotion = (data: GTMSelectPromotionData) => {
  setHomeAttribution({
    section_id: data.promotion_id,
    section_type: data.section_type || 'promotion',
    section_title: data.promotion_name,
    location_id: data.location_id || 'home',
    creative_slot: data.creative_slot,
    creative_name: data.creative_name,
    destination_url: data.destination_url,
  });
  gtmEvent({
    event: 'select_promotion',
    ...data,
  });
};

export interface GTMSelectItemData {
  item_list_id: string;
  item_list_name: string;
  items: GTMItem[];
  section_type?: string;
  location_id?: string;
}

export const gtmSelectItem = (data: GTMSelectItemData) => {
  setHomeAttribution({
    section_id: data.item_list_id,
    section_type: data.section_type || 'product-list',
    section_title: data.item_list_name,
    location_id: data.location_id || 'home',
    item_list_id: data.item_list_id,
    item_list_name: data.item_list_name,
  });
  gtmEvent({
    event: 'select_item',
    item_list_id: data.item_list_id,
    item_list_name: data.item_list_name,
    ecommerce: {
      items: data.items,
    },
    section_type: data.section_type,
    location_id: data.location_id || 'home',
  });
};

export const trackHomePromotionClick = (params: {
  section: { id?: string; type?: string; title?: string; order?: number };
  creativeSlot?: string | number;
  creativeName?: string;
  destinationUrl?: string;
}) => {
  const promotionId = resolveSectionId(params.section);
  gtmSelectPromotion({
    promotion_id: promotionId,
    promotion_name: params.section.title || params.section.type || promotionId,
    creative_slot:
      params.creativeSlot != null ? String(params.creativeSlot) : undefined,
    creative_name: params.creativeName,
    destination_url: params.destinationUrl,
    section_type: params.section.type,
    location_id: 'home',
  });
};

export const trackHomeItemSelect = (
  product: any,
  context: HomeListContext,
  quantity = 1,
) => {
  if (!context.sectionId) return;
  gtmSelectItem({
    item_list_id: context.sectionId,
    item_list_name: context.sectionTitle || context.sectionType || context.sectionId,
    items: [productToGTMItem(product, quantity)],
    section_type: context.sectionType,
    location_id: context.locationId || 'home',
  });
};
